import { Home, Building2, Calculator, AlertTriangle, Settings, Users, FileText, Sparkles, ChevronDown, ChevronRight, UserCheck, Gift } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useState } from "react";
import { Sidebar, SidebarBody, SidebarLink, useSidebar } from "@/components/ui/aceternity-sidebar";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import obrimLogo from '@/assets/obrim-logo-transparent.png';

const menuItems = [
  {
    title: "Painel",
    url: "/dashboard",
    icon: Home
  },
  {
    title: "Projetos",
    url: "/projects",
    icon: Building2
  },
  {
    title: "CRM",
    icon: Users,
    subItems: [
      { title: "Clientes", url: "/clientes" },
      { title: "Propostas", url: "/propostas" }
    ]
  },
  {
    title: "Financeiro",
    url: "/financeiro",
    icon: Calculator
  },
  {
    title: "Equipe",
    url: "/equipe",
    icon: UserCheck
  },
  {
    title: "Documentos",
    url: "/documentos",
    icon: FileText
  },
  {
    title: "Central de IA",
    url: "/ia",
    icon: Sparkles
  },
  {
    title: "Alertas",
    url: "/alertas",
    icon: AlertTriangle
  },
  {
    title: "Indicações",
    url: "/indicacoes",
    icon: Gift
  },
  {
    title: "Configurações",
    url: "/configuracoes",
    icon: Settings
  }
];

export function AppSidebar() {
  const location = useLocation();
  const currentPath = location.pathname;
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const isActive = (path: string) => currentPath === path;

  const isSubMenuActive = (subItems: any[]) => {
    return subItems?.some(subItem => isActive(subItem.url)) || false;
  };

  const toggleMenu = (menuTitle: string) => {
    const item = menuItems.find(item => item.title === menuTitle);
    if (item?.subItems && isSubMenuActive(item.subItems)) {
      return;
    }
    setExpandedMenus(prev =>
      prev.includes(menuTitle)
        ? prev.filter(title => title !== menuTitle)
        : [...prev, menuTitle]
    );
  };

  const isMenuExpanded = (menuTitle: string) => {
    const item = menuItems.find(item => item.title === menuTitle);
    return (item?.subItems && isSubMenuActive(item.subItems)) || expandedMenus.includes(menuTitle);
  };

  const links = menuItems
    .filter(item => !item.subItems)
    .map(item => ({
      label: item.title,
      href: item.url,
      icon: <item.icon className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
    }));

  const [open, setOpen] = useState(false);

  return (
    <Sidebar open={open} setOpen={setOpen}>
      <SidebarBody className="justify-between gap-10">
        <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          {open ? <Logo /> : <LogoIcon />}
          <div className="mt-8 flex flex-col gap-2">
            {menuItems.map((item) => {
              if (item.subItems) {
                return (
                  <div key={item.title}>
                    <div
                      onClick={() => toggleMenu(item.title)}
                      className={cn(
                        "flex items-center justify-start gap-2 group/sidebar py-2 cursor-pointer",
                        isSubMenuActive(item.subItems) && "text-primary"
                      )}
                    >
                      <item.icon className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
                      <motion.span
                        animate={{
                          display: open ? "inline-block" : "none",
                          opacity: open ? 1 : 0,
                        }}
                        className="text-neutral-700 dark:text-neutral-200 text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0"
                      >
                        {item.title}
                      </motion.span>
                      {open && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="ml-auto"
                        >
                          {isMenuExpanded(item.title) ? (
                            <ChevronDown className="h-4 w-4 text-neutral-700 dark:text-neutral-200" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-neutral-700 dark:text-neutral-200" />
                          )}
                        </motion.div>
                      )}
                    </div>
                    {isMenuExpanded(item.title) && open && (
                      <div className="ml-6 mt-2 space-y-2">
                        {item.subItems.map((subItem) => (
                          <SidebarLink
                            key={subItem.title}
                            link={{
                              label: subItem.title,
                              href: subItem.url,
                              icon: <div className="h-2 w-2 rounded-full bg-neutral-400 dark:bg-neutral-600" />
                            }}
                            className={cn(
                              isActive(subItem.url) && "text-primary font-semibold"
                            )}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <SidebarLink
                  key={item.title}
                  link={{
                    label: item.title,
                    href: item.url,
                    icon: <item.icon className="text-neutral-700 dark:text-neutral-200 h-5 w-5 flex-shrink-0" />
                  }}
                  className={cn(
                    isActive(item.url) && "text-primary font-semibold"
                  )}
                />
              );
            })}
          </div>
        </div>
      </SidebarBody>
    </Sidebar>
  );
}

export const Logo = () => {
  return (
    <div className="font-normal flex items-center py-1 relative z-20">
      <img
        src={obrimLogo}
        alt="Obrim - Gestão de Obras"
        className="h-8 w-auto"
      />
    </div>
  );
};

export const LogoIcon = () => {
  return (
    <div className="font-normal flex items-center py-1 relative z-20">
      <img
        src={obrimLogo}
        alt="Obrim"
        className="h-8 w-8 object-contain"
      />
    </div>
  );
};
